import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import {
	fireUIAnalytics,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	addPrioritySchemeButtonUIEvent,
	editPrioritySchemeDropdownItemUIEvent,
} from '../../common/constants';
import { TABLE_DEFAULT_PAGE, TABLE_ROWS_PER_PAGE_LIMIT } from '../../common/ui/constants';
import {
	ModalOnCompleteStatus,
	type OnEditSchemeParam,
	type PrioritySchemeModalWrapperProps,
	type PrioritySchemesPage,
	type PrioritySchemesPageState,
} from '../../common/ui/types';
import type { PrioritySchemesServiceReturn } from '../../services/priority-schemes-page';
import { useFilters } from '../filters';

type PrioritySchemeModalPartialProps = Omit<PrioritySchemeModalWrapperProps, 'onComplete'>;
type PrioritySchemeControllerParams = PrioritySchemesServiceReturn;

export type PrioritySchemesPageControllerReturn = {
	locked: boolean;
	prioritySchemeModalPartialProps: PrioritySchemeModalPartialProps;
	onComplete: (status: (typeof ModalOnCompleteStatus)[keyof typeof ModalOnCompleteStatus]) => void;
	onAddScheme: (_event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onDeleteSchemeSuccess: () => void;
	onEditScheme: (param: OnEditSchemeParam) => void;
	onFetch: (page: number) => void;
	page: PrioritySchemesPage;
	state: (typeof PrioritySchemesPageState)[keyof typeof PrioritySchemesPageState];
};

const defaultPrioritySchemeModalPartialProps = {
	initialPriorityScheme: undefined,
	isOpen: false,
	startingTab: 0,
};

export const usePrioritySchemesPageController = ({
	data,
	onRefetch,
	state,
}: PrioritySchemeControllerParams): PrioritySchemesPageControllerReturn => {
	const { filters, pageNumber, setPageNumber } = useFilters();
	const { prioritySchemes, prioritySchemesCount, taskInProgress } = data;
	const currentPageNumber = pageNumber || TABLE_DEFAULT_PAGE;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [page, setPage] = useState<PrioritySchemesPage>({
		currentPage: currentPageNumber,
		items: prioritySchemes,
		totalItems: prioritySchemesCount,
		pageSize: TABLE_ROWS_PER_PAGE_LIMIT,
	});

	const [prioritySchemeModalPartialProps, setPrioritySchemeModalPartialProps] =
		useState<PrioritySchemeModalPartialProps>(defaultPrioritySchemeModalPartialProps);

	useEffect(() => {
		onRefetch(currentPageNumber, filters);
	}, [onRefetch, filters, currentPageNumber]);

	useEffect(() => {
		setPage((prevState) => ({
			...prevState,
			items: prioritySchemes,
			totalItems: prioritySchemesCount,
			...(prevState.currentPage !== currentPageNumber && {
				currentPage: currentPageNumber,
			}),
		}));
	}, [currentPageNumber, prioritySchemes, prioritySchemesCount]);

	const onPrioritySchemeModalComplete = (
		status: (typeof ModalOnCompleteStatus)[keyof typeof ModalOnCompleteStatus],
	) => {
		if (status === ModalOnCompleteStatus.SUCCESS) {
			const { currentPage } = page;
			onRefetch(currentPage, filters);
		}
		setPrioritySchemeModalPartialProps(defaultPrioritySchemeModalPartialProps);
	};

	const onAddScheme = (_event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, addPrioritySchemeButtonUIEvent.actionSubjectId);

		setPrioritySchemeModalPartialProps((prevProps) => ({
			...prevProps,
			isOpen: !prevProps.isOpen,
		}));
	};

	const onEditScheme = ({ prioritySchemeId, startingTab }: OnEditSchemeParam) => {
		const { items } = page;
		const initialPriorityScheme = items.find(({ id }) => id === prioritySchemeId);

		if (!initialPriorityScheme) {
			return;
		}

		const { description, priorities, projects } = initialPriorityScheme;

		const editPrioritySchemeDropdownItemUIEventWithContext = {
			...editPrioritySchemeDropdownItemUIEvent,
			containers: {
				priorityScheme: {
					id: prioritySchemeId,
				},
			},
		};

		const editPrioritySchemeDropdownItemUIEventAttributes = {
			prioritySchemeId,
			priorities: priorities.map(({ id }) => id),
			prioritySchemePriorityCount: priorities.length,
			prioritySchemeHasDescription: Boolean(description),
			prioritySchemeProjects: projects.values.map(({ id }) => id),
			prioritySchemeProjectsCount: projects.total,
		};

		fireUIAnalytics(
			createAnalyticsEvent(editPrioritySchemeDropdownItemUIEventWithContext),
			editPrioritySchemeDropdownItemUIEvent.actionSubjectId,
			editPrioritySchemeDropdownItemUIEventAttributes,
		);

		setPrioritySchemeModalPartialProps((prevProps) => ({
			initialPriorityScheme,
			isOpen: !prevProps.isOpen,
			startingTab,
		}));
	};

	const onFetch = useCallback(
		(currentPage: number) => {
			onRefetch(currentPage, filters);
			setPage((prevState) => ({ ...prevState, currentPage }));
			setPageNumber(currentPage);
		},
		[onRefetch, filters, setPageNumber],
	);

	const onDeleteSchemeSuccess = () => {
		const { currentPage } = page;
		onRefetch(currentPage, filters);
	};

	return {
		locked: taskInProgress,
		prioritySchemeModalPartialProps,
		onComplete: onPrioritySchemeModalComplete,
		onAddScheme,
		onDeleteSchemeSuccess,
		onEditScheme,
		onFetch,
		page,
		state,
	};
};
